import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Button, Select, Empty, Tag, Space, message, Row, Col, Statistic } from 'antd';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import { PaymentsApi } from '../../api/clients/payments-api';
import { ProjectsApi } from '../../api/api';
import { DelayedPaymentDto, DelayedPaymentsResultDto, ProjectDto } from '../../api/models';
import RecordPaymentModal from '../deals/components/Modals/RecordPaymentModal';
import FeatherIcon from 'feather-icons-react';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper, TopToolBox } from 'container/styled';
import { CustomTableStyleWrapper } from 'pages/style';
import { AutoComplete } from 'components/autoComplete/autoComplete';
import { formatCurrency } from 'utility/utility';

const projectsApi = new ProjectsApi();

interface PaymentModalState {
  visible: boolean;
  paymentId: number | null;
  dealId: number | null;
}

const DelayedPayments: React.FC = () => {
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [data, setData] = useState<DelayedPaymentsResultDto>();
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
    projectId: undefined as number | undefined,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [paymentModal, setPaymentModal] = useState<PaymentModalState>({
    visible: false,
    paymentId: null,
    dealId: null
  });

  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const paymentsApi = new PaymentsApi();

      const result = await paymentsApi.apiPaymentsDelayedGet({
        pageNumber: query.pageNumber,
        pageSize: query.pageSize,
        searchTerm: query.searchTerm || undefined,
        projectId: query.projectId || undefined,
      });

      setData(result.data);
      setPagination({
        current: result.data.pageNumber || 1,
        pageSize: query.pageSize,
        total: result.data.totalCount || 0,
      });
    } catch (error) {
      console.error('Error fetching delayed payments:', error);
      message.error('Failed to load delayed payments');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load projects for filter dropdown
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await projectsApi.apiProjectsGet({
          pageSize: -1,
          isActive: true
        });
        setProjects(response.data.items || []);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };
    loadProjects();
  }, []);

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const handleProjectFilterChange = (value: number) => {
    setQuery(prev => ({
      ...prev,
      projectId: value === -1 ? undefined : value,
      pageNumber: 1,
    }));
  };

  const handleEmailClient = (email: string) => {
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      message.warning('No email address available for this client');
    }
  };

  const handleCallClient = (phone?: string) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      message.warning('No phone number available for this client');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getDelayedDaysTag = (days: number) => {
    if (days <= 7) {
      return <Tag color="orange">{days} days</Tag>;
    } else if (days <= 30) {
      return <Tag color="red">{days} days</Tag>;
    } else {
      return <Tag color="magenta">{days} days</Tag>;
    }
  };

  const handleRecordPayment = (cashFlowId: number, dealId: number) => {
    setPaymentModal({
      visible: true,
      paymentId: cashFlowId,
      dealId: dealId
    });
  };

  const onPaymentRecord = async () => {
    setPaymentModal({ visible: false, paymentId: null, dealId: null });
    // Refresh the data after recording payment
    await fetchData();
  };

  const columns: ColumnsType<DelayedPaymentDto> = [
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: true,
      render: (text: string, record: DelayedPaymentDto) => (
        <Button
          type="link"
          onClick={() => navigate(`/clients/${record.clientId || 0}`)}
          style={{ padding: 0, fontWeight: 'normal' }}
        >
          {text || '-'}
        </Button>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'clientEmail',
      key: 'clientEmail',
      render: (email: string) => email || '-',
    },
    {
      title: 'Deal Name',
      dataIndex: 'dealName',
      key: 'dealName',
      render: (text: string, record: DelayedPaymentDto) => (
        <Button
          type="link"
          onClick={() => navigate(`/deals/${record.dealId || 0}`)}
          style={{ padding: 0, fontWeight: 'normal' }}
        >
          {text || '-'}
        </Button>
      ),
    },
    {
      title: 'Project',
      dataIndex: 'projectName',
      key: 'projectName',
      render: (text: string, record: DelayedPaymentDto) => (
        <Button
          type="link"
          onClick={() => navigate(`/projects/${record.projectId || 0}`)}
          style={{ padding: 0, fontWeight: 'normal' }}
        >
          {text || '-'}
        </Button>
      ),
    },
    {
      title: 'Expected Amount',
      dataIndex: 'expectedAmount',
      key: 'expectedAmount',
      sorter: true,
      render: (amount: number) => amount ? formatCurrency(amount) : '-',
    },
    {
      title: 'Expected Date',
      dataIndex: 'expectedDate',
      key: 'expectedDate',
      sorter: true,
      render: (date: string) => date ? formatDate(date) : '-',
    },
    {
      title: 'Delayed Days',
      dataIndex: 'delayedDays',
      key: 'delayedDays',
      sorter: true,
      render: (days: number) => days ? getDelayedDaysTag(days) : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record: DelayedPaymentDto) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleRecordPayment(record.cashFlowId!, record.dealId!)}
            icon={<FeatherIcon icon="check-circle" size={16} />}
          >
          </Button>
          {record.clientEmail && record.clientEmail !== '' && (
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => handleEmailClient(record.clientEmail || '')}
              title="Send Email"
            />
          )}
          {record.clientPhone && record.clientPhone !== '' && (
            <Button
              type="text"
              icon={<PhoneOutlined />}
              onClick={() => handleCallClient(record.clientPhone || undefined)}
              title="Call Client"
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('payments:delayed-payments', 'Delayed Payments')}
        />
      </CardToolbox>

      <Main>
        {/* Overview Cards */}
        {data?.overview && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Cards>
                <Statistic
                  title={t('payments:total-delayed-amount', 'Total Delayed Amount')}
                  value={data.overview.totalDelayedAmount || 0}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<FeatherIcon icon="alert-circle" size={16} />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Cards>
            </Col>
            {query.projectId && data.overview.totalDelayedAmountByProject && data.overview.totalDelayedAmountByProject > 0 && (
              <Col xs={24} sm={12} lg={6}>
                <Cards>
                  <Statistic
                    title={t('payments:project-delayed-amount', 'Selected Project Amount')}
                    value={data.overview.totalDelayedAmountByProject}
                    formatter={(value) => formatCurrency(Number(value))}
                    prefix={<FeatherIcon icon="briefcase" size={16} />}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Cards>
              </Col>
            )}
            <Col xs={24} sm={12} lg={6}>
              <Cards>
                <Statistic
                  title={t('payments:count-delayed-payments', 'Delayed Payments')}
                  value={data.overview.countDelayedPayments || 0}
                  prefix={<FeatherIcon icon="clock" size={16} />}
                />
              </Cards>
            </Col>
            {data.overview.mostDelayedPaymentClientName && (
              <Col xs={24} sm={12} lg={6}>
                <Cards>
                  <Statistic
                    title={t('payments:most-delayed-payment', 'Most Delayed Payment')}
                    value={data.overview.mostDelayedPaymentAmount || 0}
                    formatter={(value) => formatCurrency(Number(value))}
                    prefix={<FeatherIcon icon="user-x" size={16} />}
                    valueStyle={{ color: '#d4380d' }}
                  />
                  <div style={{ marginTop: 8, fontSize: 12 }}>
                    {data.overview.mostDelayedPaymentClientId ? (
                      <Link to={`/clients/${data.overview.mostDelayedPaymentClientId}`}>
                        {data.overview.mostDelayedPaymentClientName}
                      </Link>
                    ) : (
                      data.overview.mostDelayedPaymentClientName
                    )}
                    {' - '}
                    <Tag color="magenta">{data.overview.mostDelayedPaymentDays} days</Tag>
                  </div>
                </Cards>
              </Col>
            )}
          </Row>
        )}

        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={handleSearch}
                        width="100%"
                        patterns
                        placeholder={t('payments:search', 'Search by client, deal, email...')}
                      />
                    </div>
                  </Col>

                  <Col lg={12} md={14} xs={24}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{t('projects:project-filter', 'Project:')}</span>
                        <Select
                          style={{ width: 200 }}
                          onChange={handleProjectFilterChange}
                          value={query.projectId ?? -1}
                          loading={projectsLoading}
                        >
                          <Select.Option value={-1}>{t('global:all', 'All')}</Select.Option>
                          {projects.map(project => (
                            <Select.Option key={project.id} value={project.id}>
                              {project.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <CustomTableStyleWrapper>
            <TableWrapper>
              <Table
                loading={loading}
                dataSource={data?.items || []}
                columns={columns}
                pagination={{
                  current: query.pageNumber,
                  pageSize: query.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 50, 100],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                  onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                }}
                rowKey="cashFlowId"
                locale={{
                  emptyText: (
                    <Empty
                      description={t('payments:no-delayed-payments', 'No delayed payments found')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            </TableWrapper>
          </CustomTableStyleWrapper>
        </Cards>
      </Main>

      {paymentModal.visible && paymentModal.paymentId && paymentModal.dealId && (
        <RecordPaymentModal
          visible={paymentModal.visible}
          dealId={paymentModal.dealId}
          paymentId={paymentModal.paymentId}
          onSuccess={onPaymentRecord}
          onCancel={() => setPaymentModal({ visible: false, paymentId: null, dealId: null })}
        />
      )}
    </>
  );
};

export default DelayedPayments;